import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { BottomSheet, Box, Card, Column, Popover, SegmentControl, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { ColorPicker } from '~/components/ColorPicker/ColorPicker';
import { ColorSelector } from '~/components/ColorSelector/ColorSelector';
import { useLocaleKeys } from '~/i18n';
import styles from './BrandingControllers.module.scss';
import { PRESET_GRADIENTS, PRESET_SOLID_COLORS } from './brandingPresetColors';
import stylesPrimary from './PrimaryBackgroundColorPopup.module.scss';
export const GetPrimaryBackgroundColorPopupTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export function PrimaryBackgroundColorPopup(props) {
    const { branding, hasError, size, useMobileSheet, mobileSheetTitle, testId, onChange } = props;
    const { t: texts } = useLocaleKeys('clientFlow.brandingColors');
    const { t: brandingTexts } = useLocaleKeys('clientFlow.branding');
    const tids = useMemo(()=>GetPrimaryBackgroundColorPopupTID(testId), [
        testId
    ]);
    const { PrimaryBackgroundColorPopupTestIds: tid } = tids;
    const start = branding.backgroundGradientStart;
    const end = branding.backgroundGradientEnd;
    const isSolid = start === end;
    const [anchorEl, setAnchorEl] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(isSolid ? 'solid' : 'gradient');
    const displayBackground = isSolid ? start : "linear-gradient(315deg, ".concat(end, " 0%, ").concat(start, " 100%)");
    function applySolid(color) {
        onChange(_object_spread_props(_object_spread({}, branding), {
            backgroundGradientStart: color,
            backgroundGradientEnd: color
        }));
    }
    function applyGradient(color1, color2) {
        onChange(_object_spread_props(_object_spread({}, branding), {
            backgroundGradientStart: color1,
            backgroundGradientEnd: color2
        }));
    }
    const handleClick = (e)=>{
        if (useMobileSheet) {
            setSheetOpen(true);
        } else {
            setAnchorEl(e.currentTarget);
        }
    };
    const popupContent = /*#__PURE__*/ _jsxs(Column, {
        gap: 16,
        alignItems: "stretch",
        children: [
            /*#__PURE__*/ _jsx(Box, {
                className: stylesPrimary.segmentControlWrapper,
                children: /*#__PURE__*/ _jsx(SegmentControl, {
                    options: [
                        {
                            value: 'gradient',
                            label: brandingTexts.gradient(),
                            testId: tid.GradientTab()
                        },
                        {
                            value: 'solid',
                            label: brandingTexts.solid(),
                            testId: tid.SolidTab()
                        }
                    ],
                    value: activeTab,
                    onChange: (value)=>setActiveTab(value),
                    variant: "text",
                    "aria-label": texts.backgroundType(),
                    testId: tid.Segment()
                })
            }),
            activeTab === 'gradient' ? /*#__PURE__*/ _jsxs(Column, {
                gap: 24,
                className: stylesPrimary.tabContent,
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        className: clsx(stylesPrimary.gradientGrid, useMobileSheet && styles.bottomSheetGradientGrid),
                        children: PRESET_GRADIENTS.map((param, idx)=>{
                            let [c1, c2] = param;
                            return /*#__PURE__*/ _jsx(ColorSelector, {
                                withBorder: true,
                                color: "linear-gradient(315deg, ".concat(c2, " 0%, ").concat(c1, " 100%)"),
                                selected: start === c1 && end === c2,
                                testId: "".concat(tid.GradientPreset(), "-").concat(idx),
                                onClick: ()=>applyGradient(c1, c2)
                            }, "".concat(c1, "-").concat(c2, "-").concat(idx));
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        gap: 24,
                        children: [
                            /*#__PURE__*/ _jsx(ColorPicker, {
                                colorAsSuffix: true,
                                className: stylesPrimary.colorPickerFullWidth,
                                inputWrapperClassName: stylesPrimary.primaryBgColorPickerInputWrapper,
                                testId: tid.CustomColor1(),
                                label: texts.customColor1(),
                                color: start,
                                displayColor: start,
                                noMinWidth: true,
                                onChange: (c)=>applyGradient(c, end)
                            }),
                            /*#__PURE__*/ _jsx(ColorPicker, {
                                colorAsSuffix: true,
                                className: stylesPrimary.colorPickerFullWidth,
                                inputWrapperClassName: stylesPrimary.primaryBgColorPickerInputWrapper,
                                label: texts.customColor2(),
                                testId: tid.CustomColor2(),
                                color: end,
                                displayColor: end,
                                noMinWidth: true,
                                onChange: (c)=>applyGradient(start, c)
                            })
                        ]
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(Column, {
                gap: 24,
                className: stylesPrimary.tabContent,
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        className: clsx(stylesPrimary.gradientGrid, useMobileSheet && styles.bottomSheetSolidGrid),
                        children: PRESET_SOLID_COLORS.map((presetColor, idx)=>/*#__PURE__*/ _jsx(ColorSelector, {
                                withBorder: true,
                                color: presetColor,
                                selected: presetColor.toLowerCase() === (start || '').toLowerCase(),
                                testId: "".concat(tid.SolidPreset(), "-").concat(idx),
                                onClick: ()=>applySolid(presetColor)
                            }, presetColor))
                    }),
                    /*#__PURE__*/ _jsx(ColorPicker, {
                        colorAsSuffix: true,
                        className: stylesPrimary.colorPickerFullWidth,
                        inputWrapperClassName: stylesPrimary.primaryBgColorPickerInputWrapper,
                        testId: tid.SolidCustom(),
                        color: start,
                        noMinWidth: true,
                        label: texts.customColor(),
                        displayColor: start,
                        onChange: applySolid
                    })
                ]
            })
        ]
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ColorSelector, {
                withBorder: true,
                color: displayBackground,
                hasError: hasError,
                size: size,
                testId: tid.Trigger(),
                onClick: handleClick
            }),
            useMobileSheet ? /*#__PURE__*/ _jsx(BottomSheet, {
                open: sheetOpen,
                onClose: ()=>setSheetOpen(false),
                children: /*#__PURE__*/ _jsxs(Column, {
                    className: styles.bottomSheetContent,
                    gap: 24,
                    children: [
                        mobileSheetTitle && /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            weight: "normal",
                            className: styles.bottomSheetTitle,
                            children: mobileSheetTitle
                        }),
                        popupContent
                    ]
                })
            }) : /*#__PURE__*/ _jsx(Popover, {
                anchorEl: anchorEl,
                open: !!anchorEl,
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right'
                },
                transformOrigin: {
                    vertical: -5,
                    horizontal: 'right'
                },
                onClose: ()=>setAnchorEl(null),
                children: /*#__PURE__*/ _jsx(Card, {
                    className: stylesPrimary.popupCard,
                    children: popupContent
                })
            })
        ]
    });
}
